/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ContentVodItemKernel,
    FolderVodItemKernel,
    OfferVodItemKernel,
    ScreenType,
    UnifiedCollectionVodItemKernel,
    VodItem,
    VodItemKernelType,
    VodItemList,
    VodItemType,
} from 'client-code/vod-browse';

import { DeviceModel } from '../../../core/device';
import { BrandingUiInfoKeys } from '../../../core/entities';
import { ContentType, InteractiveComponents } from '../../../core/enums';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import { IInfoCard, InfoCardManager } from '../../../core/infocard';
import { NdvrInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import type { AppDispatch, TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { TListViewType, TVodPlayActionStateType } from '../../../core/types';
import { VodBrowseInfoCardUserInputHandler } from '../../../core/usecases/VodBrowseInfoCardUserInputHandler';
import { BrandingUiUtility } from '../../../core/utils';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import {
    biaxialWidgetClearDataAction,
    biaxialWidgetUpdateHorizontalItemsAction,
    biaxialWidgetUpdateHorizontalPositionAction,
} from '../../biaxialwidget/biaxialwidget.actions';
import { BWHListType, SCROLL_DIRECTION } from '../../biaxialwidget/biaxialwidget.props';
import {
    biaxialWidgetDisableChannelPageScroll,
    biaxialWidgetDisableTurboScroll,
    biaxialWidgetEnableChannelPageScroll,
    biaxialWidgetEnableTurboScroll,
} from '../../biaxialwidget/biaxialwidget.reducers';
import {
    onKeyChannelDownBiaxialWidget,
    onKeyChannelUpBiaxialWidget,
    onKeyDownBiaxialWidget,
    onKeyLeftBiaxialWidget,
    onKeyRightBiaxialWidget,
    onKeyUpBiaxialWidget,
    onMouseOutBiaxialWidget,
    onMouseOverBiaxialWidget,
} from '../../biaxialwidget/navigation/biaxialwidgetnavigation.reducers';
import {
    setListEntryActions,
    setListEntryPageActions,
} from '../../listscreenview/listviewscreen.actions';
import {
    onKeyLeftOnePassOptionsOverlay,
    onKeyOkDOCOverlay,
    onKeyOkOnePassOptionsOverlay,
    onKeyRightOnePassOptionsOverlay,
    onMouseClickDOCOptionsOverlay,
    onMouseClickOnePassOptionsOverlay,
} from '../../overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onKeyLeftRecordingOverlay,
    onKeyOkRecordingOverlay,
    onKeyOkStopOrCancelRecording,
    onKeyRightRecordingOverlay,
    onMouseClickRecordingOverlay,
    onMouseClickStopOrCancelRecording,
} from '../../overlay/recordingoverlay/recordingoverlay.reducers';
import {
    onKeyOkStorageLimitOverlay,
    onMouseClickStorageLimitOverlay,
} from '../../overlay/storagelimitoverlay/storagelimitoverlay.reducers';
import {
    onKeyOkVodPlayErrorOverlay,
    onMouseClickVodPlayErrorOverlay,
} from '../../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import {
    onKeyOkVodUpSellOverlay,
    onMouseClickVodUpSellOverlay,
} from '../../overlay/vodupselloverlay/vodupselloverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../overlaydialog/overlaydialog.reducers';
import {
    collectionScreenTransition,
    galleryScreenTransition,
    programScreenTransition,
} from '../../screens/navigation/screensnavigation';
import {
    showGuideScreen,
    showListViewScreen,
    showPreviousScreen,
} from '../../screens/screens.reducers';
import { notifyVodPlayActionState } from '../../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    vodBrowseMainScreenClearDataAction,
    vodBrowseMainScreenUpdateHorizontalPositionsAction,
} from '../vodbrowsemainscreen.actions';
import { vodBrowseMainScreenUpdateHorizontalItems } from '../vodbrowsemainscreen.reducers';
import { NAVIGATION_DIRECTION, TNavigationDirection } from './vodbrowsemainscreennavigation.props';

const TAG: string = 'vodbrowsemainscreennavigation.reducers';

const overlayKeyHandler = (
    screenTaskController: string,
    dispatch: Function,
    callback: Function,
) => {
    const overlayTaskController: string[] = [
        ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS,
        ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS,
        ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS,
        ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK,
        ScreenTaskControllerTypes.SLO_TASKS,
        ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS,
        ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS,
        ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS,
        ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY,
        ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS,
        ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY,
        ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS,
    ];

    if (overlayTaskController.includes(screenTaskController)) {
        dispatch(callback());
    }
};

// Helper function for handling mouse click navigation on default screen
const handleDefaultScreenMouseClick = (value: string) => (dispatch: Function) => {
    switch (value) {
        case InteractiveComponents.EAST:
            dispatch(onKeyRightVodBrowseMainScreen());
            break;
        case InteractiveComponents.NORTH:
            dispatch(onKeyUpVodBrowseMainScreen());
            break;
        case InteractiveComponents.OK:
            dispatch(onKeyOkVodBrowseMainScreen());
            break;
        case InteractiveComponents.SOUTH:
            dispatch(onKeyDownVodBrowseMainScreen());
            break;
        case InteractiveComponents.WEST:
            dispatch(onKeyLeftVodBrowseMainScreen());
            break;
        default:
    }
};

// Helper function for handling vertical navigation
const handleVerticalNavigation =
    (direction: TNavigationDirection) => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { horizontalPositions, verticalItems } = getState().vodBrowseMainScreen;

        const isUpDirection = direction === NAVIGATION_DIRECTION.UP;
        const positionIncrement = isUpDirection ? -1 : 1;
        const newVerticalPosition = verticalPosition + positionIncrement;

        // Check bounds with descriptive variables
        const minValidPosition = 0;
        const maxValidPosition = verticalItems.length - 1;
        const isOutOfBounds = isUpDirection
            ? newVerticalPosition < minValidPosition
            : newVerticalPosition > maxValidPosition;

        if (isOutOfBounds) {
            Log.i(TAG, `No more items to navigate ${direction}.`);
            return;
        }

        dispatch(
            biaxialWidgetUpdateHorizontalItemsAction({
                listType: BWHListType.VOD_BROWSE_MAIN,
                listItems: [],
                isEmpty: false,
            }),
        );

        // Update positions and dispatch actions
        horizontalPositions[verticalPosition] = horizontalPosition;
        dispatch(
            biaxialWidgetUpdateHorizontalPositionAction(horizontalPositions[newVerticalPosition]),
        );
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));

        const parentVodItemId: string =
            verticalItems?.[newVerticalPosition]?.vodItemKernel?.vodItemId ?? '';
        dispatch(vodBrowseMainScreenUpdateHorizontalItems(parentVodItemId, newVerticalPosition));

        const biaxialNavigationAction = isUpDirection
            ? onKeyUpBiaxialWidget()
            : onKeyDownBiaxialWidget();
        dispatch(biaxialNavigationAction);
    };

// Key Navigations
const handleOnKeyOkVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
    const { horizontalItems } = getState().vodBrowseMainScreen;
    const selectedHStrip: VodItemList = horizontalItems?.[verticalPosition];
    const selectedItem: VodItem | undefined = selectedHStrip?.vodItemList?.[horizontalPosition];
    if (!selectedItem) {
        Log.i(TAG, 'No item selected on OK key press.');
        return;
    }

    if (NetworkManager.isNetworkDisconnected()) {
        const service = new NdvrInteractor();
        const message = BrandingUiUtility.formatDescription(
            Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
            {
                DEVICE_NAME_SHORT: service.getBrandingUiInfoForKey(
                    BrandingUiInfoKeys.DEVICE_NAME_SHORT,
                ),
                BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                    ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                    : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
            },
        );
        dispatch(showWhisper({ message, iconType: WhisperTypes.INFO }));
        return;
    }

    const { vodItemKernel, vodItemType, title } = selectedItem;

    switch (vodItemType.toLowerCase()) {
        case VodItemType.FolderVodItem.toLowerCase(): {
            const { isAdult, vodItemId, partnerId, screenType } =
                vodItemKernel as FolderVodItemKernel;
            if (screenType === ScreenType.TextCurrentRentals) {
                dispatch(setListEntryActions(0));
                dispatch(setListEntryPageActions(0));
                dispatch(
                    showListViewScreen({
                        id: vodItemId ?? '',
                        title: title ?? '',
                        isAdult: !!isAdult,
                        listType: screenType as TListViewType,
                    }),
                );
                Log.i(TAG, `Navigating to list view screen with id: ${vodItemId}`);
            } else {
                dispatch(
                    galleryScreenTransition(
                        !!isAdult,
                        vodItemId ?? '',
                        partnerId,
                        screenType,
                        title ?? '',
                    ),
                );

                Log.i(TAG, `Navigating to gallery screen with parentVodItemId: ${vodItemId}`);
            }
            break;
        }

        case VodItemType.EpisodeVodItem.toLowerCase():
        case VodItemType.MovieVodItem.toLowerCase():
        case VodItemType.SpecialVodItem.toLowerCase(): {
            if (
                vodItemKernel?.vodItemKernelType?.toLowerCase() ===
                VodItemKernelType.OfferVodItemKernel.toLowerCase()
            ) {
                const { partnerId, contentSupplierPartnerId, offerId } =
                    vodItemKernel as OfferVodItemKernel;

                dispatch(
                    programScreenTransition(
                        {
                            id: offerId ?? '',
                            contentType: ContentType.VOD_OFFER_ID,
                            partnerId: partnerId ?? '',
                            contentSupplierPartnerId: contentSupplierPartnerId ?? '',
                        },
                        showWhisper,
                    ),
                );
                Log.i(TAG, `Navigating to program screen with offerId: ${offerId}`);
            } else {
                const { contentId, partnerId, contentSupplierPartnerId } =
                    vodItemKernel as ContentVodItemKernel;
                dispatch(
                    programScreenTransition(
                        {
                            id: contentId ?? '',
                            contentType: ContentType.CONTENT_ID,
                            partnerId: partnerId ?? '',
                            contentSupplierPartnerId: contentSupplierPartnerId ?? '',
                        },
                        showWhisper,
                    ),
                );
                Log.i(TAG, `Navigating to program screen with contentId: ${contentId}`);
            }
            break;
        }
        case VodItemType.UnifiedCollectionVodItem.toLowerCase(): {
            const { partnerId, unifiedCollectionId, collapsedVodItemId, contentSupplierPartnerId } =
                vodItemKernel as UnifiedCollectionVodItemKernel;
            dispatch(
                collectionScreenTransition(
                    {
                        unifiedCollectionId,
                        transitionScreen: '' as TransitionScreenType,
                        partnerId,
                        collapsedVodItemId,
                        contentSupplierPartnerId,
                    },
                    showWhisper,
                ),
            );
            Log.i(
                TAG,
                `Navigating to collection screen with unifiedCollectionId: ${unifiedCollectionId}`,
            );
            break;
        }
        default:
    }
};

// Channel page navigation handler
const channelPageNavigationHandler =
    (isUpward: boolean) => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { verticalItems, horizontalPositions } = getState().vodBrowseMainScreen;

        if (!verticalItems || verticalItems.length === 0) {
            Log.i(TAG, 'No vertical items available for navigation.');
            return;
        }

        dispatch(
            biaxialWidgetUpdateHorizontalItemsAction({
                listType: BWHListType.VOD_BROWSE_MAIN,
                listItems: [],
                isEmpty: false,
            }),
        );

        horizontalPositions[verticalPosition] = horizontalPosition;

        const biaxialNavigationAction = isUpward
            ? onKeyChannelUpBiaxialWidget()
            : onKeyChannelDownBiaxialWidget();
        dispatch(biaxialNavigationAction);

        // Read the updated verticalPosition after biaxial handler ran
        const { verticalPosition: updatedVerticalPosition } = getState().biaxialWidget;

        const newHorizontalPosition = horizontalPositions?.[updatedVerticalPosition] ?? 0;

        dispatch(biaxialWidgetUpdateHorizontalPositionAction(newHorizontalPosition));
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));

        const parentVodItemId: string =
            verticalItems?.[updatedVerticalPosition]?.vodItemKernel?.vodItemId ?? '';
        dispatch(
            vodBrowseMainScreenUpdateHorizontalItems(parentVodItemId, updatedVerticalPosition),
        );
    };

export const onKeyBackVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
        case ScreenTaskControllerTypes.VOD_BROWSE_ERROR_OVERLAY_TASKS:
            dispatch(vodBrowseMainScreenClearDataAction());
            dispatch(biaxialWidgetClearDataAction());
            dispatch(showPreviousScreen());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        default:
            overlayKeyHandler(screenTaskController, dispatch, hideOverlayDialog);
    }
};

export const onKeyChannelUpVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
                dispatch(channelPageNavigationHandler(true));
                break;
            default:
        }
    };

export const onKeyChannelDownVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
                dispatch(channelPageNavigationHandler(false));
                break;
            default:
        }
    };

export const onKeyLongChannelUpStartVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { horizontalPositions } = getState().vodBrowseMainScreen;

        if (verticalPosition === 0) {
            Log.i(TAG, `Already at the top, cannot page up.`);
            return;
        }

        horizontalPositions[verticalPosition] = horizontalPosition;
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));

        // Start continuous page-by-page scrolling upward
        dispatch(biaxialWidgetEnableChannelPageScroll(SCROLL_DIRECTION.UP));
    };

export const onKeyLongChannelUpStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetDisableChannelPageScroll());
};

export const onKeyLongChannelDownStartVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { horizontalPositions, verticalItems } = getState().vodBrowseMainScreen;

        const isOutOfBounds = verticalPosition >= verticalItems.length - 1;
        if (isOutOfBounds) {
            Log.i(TAG, `Already at the bottom, cannot page down.`);
            return;
        }
        horizontalPositions[verticalPosition] = horizontalPosition;
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));

        // Start continuous page-by-page scrolling downward
        dispatch(biaxialWidgetEnableChannelPageScroll(SCROLL_DIRECTION.DOWN));
    };

export const onKeyLongChannelDownStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetDisableChannelPageScroll());
};

export const onKeyDigitVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyDigitPcPinOverlay());
    }
};

export const onKeyDownVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(handleVerticalNavigation(NAVIGATION_DIRECTION.DOWN));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        default:
            overlayKeyHandler(screenTaskController, dispatch, onKeyDownOverlayDialog);
    }
};

export const onKeyGuideVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(showGuideScreen());
    Log.i(TAG, 'Navigating to Guide Screen from Vod Browse Main Screen.');
};

export const onKeyInfoVodBrowseMainScreen = () => (dispatch: AppDispatch, getState: TGetState) => {
    // TODO: Remove Info Card CCB switch once feature is completed
    if (
        !CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
        )
    )
        return;
    const { screenTaskController } = getState().screens;
    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS)
        return;
    const { horizontalItems } = getState().vodBrowseMainScreen;
    const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
    const selectedHStrip: VodItemList = horizontalItems?.[verticalPosition];
    const selectedItem: VodItem | undefined = selectedHStrip?.vodItemList?.[horizontalPosition];
    if (!selectedItem) {
        Log.i(TAG, 'No item selected on Info key press.');
        return;
    }
    if (selectedItem?.vodItemType?.toLowerCase() === VodItemType.FolderVodItem.toLowerCase()) {
        Log.i(TAG, 'Info Card is not shown for Folder Vod Items.');
        return;
    }

    const nonFolderItems =
        selectedHStrip?.vodItemList?.filter(
            (item) => item?.vodItemType?.toLowerCase() !== VodItemType.FolderVodItem.toLowerCase(),
        ) || [];
    const infoCard: IInfoCard = InfoCardManager.getInstance();
    const vodBrowseInfoCardUserInputHandler = new VodBrowseInfoCardUserInputHandler();
    infoCard.addUserInputEventHandler(vodBrowseInfoCardUserInputHandler);
    infoCard.setProperties({
        hasItemsBefore: nonFolderItems.length > 1,
        hasItemsAfter: nonFolderItems.length > 1,
    });
    infoCard.setContextData(VodBrowseInfoCardUserInputHandler.name, {
        activeItemIndex: horizontalPosition,
        itemList: selectedHStrip.vodItemList,
    });
    infoCard.setInfoCardKernel(vodBrowseInfoCardUserInputHandler.getInfoCardKernel(selectedItem));
    infoCard.show();
};

export const onKeyLeftVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(onKeyLeftBiaxialWidget());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyLeftOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyLeftRecordingOverlay());
            break;
        default:
    }
};
export const onKeyLongDownStartVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { horizontalPositions, verticalItems } = getState().vodBrowseMainScreen;

        const isOutOfBounds = verticalPosition + 1 >= verticalItems.length;
        if (isOutOfBounds) {
            Log.i(TAG, `No more items to navigate ${SCROLL_DIRECTION.DOWN}.`);
            return;
        }

        horizontalPositions[verticalPosition] = horizontalPosition;
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));
        dispatch(biaxialWidgetEnableTurboScroll(SCROLL_DIRECTION.DOWN));
    };

export const onKeyLongDownStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(stopVerticalTurboScroll());
};

export const onKeyLongLeftStartVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetEnableTurboScroll(SCROLL_DIRECTION.LEFT));
};

export const onKeyLongLeftStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetDisableTurboScroll());
};

export const onKeyLongOkVodBrowseMainScreen = () => (dispatch: AppDispatch) => {
    dispatch(onKeyInfoVodBrowseMainScreen());
};

export const onKeyLongRightStartVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetEnableTurboScroll(SCROLL_DIRECTION.RIGHT));
};

export const onKeyLongRightStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(biaxialWidgetDisableTurboScroll());
};

export const onKeyLongUpStartVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
        const { horizontalPositions } = getState().vodBrowseMainScreen;

        const isOutOfBounds = verticalPosition - 1 < 0;
        if (isOutOfBounds) {
            Log.i(TAG, `No more items to navigate ${SCROLL_DIRECTION.UP}.`);
            return;
        }

        horizontalPositions[verticalPosition] = horizontalPosition;
        dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));
        dispatch(biaxialWidgetEnableTurboScroll(SCROLL_DIRECTION.UP));
    };

export const onKeyLongUpStopVodBrowseMainScreen = () => (dispatch: Function) => {
    dispatch(stopVerticalTurboScroll());
};

export const onKeyOkVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(handleOnKeyOkVodBrowseMainScreen());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_BROWSE_ERROR_OVERLAY_TASKS:
            dispatch(showPreviousScreen());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onKeyOkVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyOkVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkDOCOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyOkOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onKeyOkStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkRecordingOverlay());
            break;
        default:
    }
};

export const onKeyPlayVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS) {
        return;
    }
    const { verticalPosition, horizontalPosition } = getState().biaxialWidget;
    const { horizontalItems, verticalItems } = getState().vodBrowseMainScreen;

    if (!horizontalItems || !verticalItems) {
        Log.i(TAG, 'No horizontal or vertical items available for play action.');
        return;
    }

    const selectedHStrip: VodItemList = horizontalItems?.[verticalPosition];
    const selectedItem: VodItem | undefined = selectedHStrip?.vodItemList?.[horizontalPosition];

    if (!selectedItem) {
        Log.i(TAG, 'No item selected for play action.');
        return;
    }

    const { vodItemType, vodItemKernel } = selectedItem;

    // Check if the selected item is a playable VOD item type
    const playableVodItemTypes = [
        VodItemType.EpisodeVodItem.toLowerCase(),
        VodItemType.MovieVodItem.toLowerCase(),
        VodItemType.SpecialVodItem.toLowerCase(),
        VodItemType.UnifiedCollectionVodItem.toLowerCase(),
    ];

    if (!playableVodItemTypes.includes(vodItemType.toLowerCase())) {
        Log.i(TAG, `Selected item type ${vodItemType} is not playable.`);
        return; // Exit early if not a playable VOD item type
    }

    // Handle different playable item types
    if (
        vodItemKernel?.vodItemKernelType?.toLowerCase() ===
        VodItemKernelType.UnifiedCollectionVodItemKernel.toLowerCase()
    ) {
        const { unifiedCollectionId, partnerId, collapsedVodItemId } =
            vodItemKernel as UnifiedCollectionVodItemKernel;
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.INIT,
                vodRequiredParams: {
                    id: unifiedCollectionId ?? '',
                    itemType: 'unifiedCollection',
                    partnerId: partnerId ?? '',
                    collapsedVodItemId: collapsedVodItemId ?? '',
                },
            }),
        );
    } else if (
        vodItemKernel?.vodItemKernelType?.toLowerCase() ===
        VodItemKernelType.OfferVodItemKernel.toLowerCase()
    ) {
        const { offerId, partnerId } = vodItemKernel as OfferVodItemKernel;
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.INIT,
                vodRequiredParams: {
                    id: offerId ?? '',
                    itemType: 'offer',
                    partnerId: partnerId ?? '',
                },
            }),
        );
    } else {
        const { contentId, partnerId, collapsedVodItemId } = vodItemKernel as ContentVodItemKernel;
        dispatch(
            notifyVodPlayActionState({
                type: TVodPlayActionStateType.INIT,
                vodRequiredParams: {
                    id: contentId ?? '',
                    itemType: 'content',
                    partnerId: partnerId ?? '',
                    collapsedVodItemId: collapsedVodItemId ?? '',
                },
            }),
        );
    }
};

export const onKeyRightVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(onKeyRightBiaxialWidget());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyRightOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyRightRecordingOverlay());
            break;
        default:
    }
};

export const onKeyUpVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(handleVerticalNavigation(NAVIGATION_DIRECTION.UP));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        default:
            overlayKeyHandler(screenTaskController, dispatch, onKeyUpOverlayDialog);
    }
};

// Mouse Navigations
export const onMouseClickVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;
    const { inputFactory } = getState();
    const { value } = inputFactory;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(handleDefaultScreenMouseClick(value));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseClickVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseClickVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickDOCOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onMouseClickOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onMouseClickStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickRecordingOverlay());
            break;
        default:
    }
};

export const onMouseOkLongPressStartVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { inputFactory } = getState();
        const { value } = inputFactory;
        switch (value) {
            case InteractiveComponents.EAST:
                dispatch(onKeyLongRightStartVodBrowseMainScreen());
                break;
            case InteractiveComponents.NORTH:
                dispatch(onKeyLongUpStartVodBrowseMainScreen());
                break;
            case InteractiveComponents.OK:
                dispatch(onKeyLongOkVodBrowseMainScreen());
                break;
            case InteractiveComponents.SOUTH:
                dispatch(onKeyLongDownStartVodBrowseMainScreen());
                break;
            case InteractiveComponents.WEST:
                dispatch(onKeyLongLeftStartVodBrowseMainScreen());
                break;
            default:
        }
    };

export const onMouseOkLongPressStopVodBrowseMainScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { inputFactory } = getState();
        const { value } = inputFactory;
        switch (value) {
            case InteractiveComponents.EAST:
                dispatch(onKeyLongRightStopVodBrowseMainScreen());
                break;
            case InteractiveComponents.NORTH:
                dispatch(onKeyLongUpStopVodBrowseMainScreen());
                break;
            case InteractiveComponents.SOUTH:
                dispatch(onKeyLongDownStopVodBrowseMainScreen());
                break;
            case InteractiveComponents.WEST:
                dispatch(onKeyLongLeftStopVodBrowseMainScreen());
                break;
            default:
        }
    };

export const onMouseOutVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(onMouseOutBiaxialWidget());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        default:
            overlayKeyHandler(screenTaskController, dispatch, onMouseOutOverlayDialogOperations);
    }
};

export const onMouseOverVodBrowseMainScreen = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VOD_BROWSE_MAIN_SCREEN_TASKS:
            dispatch(onMouseOverBiaxialWidget());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        default:
            overlayKeyHandler(screenTaskController, dispatch, onMouseOverOverlayDialogOperations);
    }
};

const stopVerticalTurboScroll = () => (dispatch: Function, getState: TGetState) => {
    const { verticalPosition } = getState().biaxialWidget;
    const { verticalItems } = getState().vodBrowseMainScreen;

    dispatch(biaxialWidgetDisableTurboScroll());
    const parentVodItemId: string =
        verticalItems?.[verticalPosition]?.vodItemKernel?.vodItemId ?? '';
    dispatch(vodBrowseMainScreenUpdateHorizontalItems(parentVodItemId, verticalPosition));
};
